/**
 * A customer picker for the office requests inbox (tracker item 45): a filter box
 * over the customers loaded so far and a select, with "Load more customers" for a
 * long list (the customer API pages 100 at a time and has no search).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAPIClient, ICustomersApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, secondaryButtonClass } from './formStyles';

const PAGE = 100;

interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (customerId: string, customer: types.Customer | null) => void;
  disabled?: boolean;
  /** Customers to offer first (e.g. the candidates of an access request), by id */
  preferredIds?: string[];
}

export function CustomerPicker({ id, label, value, onChange, disabled, preferredIds = [] }: Props) {
  const api = useMemo<ICustomersApi>(() => createAPIClient(), []);
  const [customers, setCustomers] = useState<types.Customer[]>([]);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNext = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = page + 1;
      const result = await api.listCustomers(next, PAGE);
      setCustomers((current) => [...current, ...result.data.filter((c) => !current.some((known) => known.id === c.id))]);
      setPage(next);
      setPages(Math.max(1, result.pagination.pages));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load customers'));
    } finally {
      setLoading(false);
    }
  }, [api, page]);

  useEffect(() => {
    if (page === 0) void loadNext();
    // Load the first page once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needle = filter.trim().toLowerCase();
  const visible = customers
    .filter((c) => !needle || c.name.toLowerCase().includes(needle) || (c.email ?? '').toLowerCase().includes(needle) || c.id === value)
    .sort((a, b) => Number(preferredIds.includes(b.id)) - Number(preferredIds.includes(a.id)) || a.name.localeCompare(b.name));

  return (
    <div>
      <label htmlFor={`${id}-filter`} className={labelClass}>{label}</label>
      <input
        id={`${id}-filter`} type="search" className={`${inputClass} mb-2`} placeholder="Type to filter by name or email" value={filter}
        onChange={(e) => setFilter(e.target.value)} disabled={disabled} aria-label={`Filter ${label.toLowerCase()}`}
      />
      <select
        id={id} className={`${inputClass} min-h-[44px]`} value={value} disabled={disabled}
        onChange={(e) => onChange(e.target.value, customers.find((c) => c.id === e.target.value) ?? null)}
        aria-label={label}
      >
        <option value="">Choose a customer</option>
        {visible.map((c) => <option key={c.id} value={c.id}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
      </select>
      {error && <p role="alert" className="mt-1 text-xs text-red-700">{error}</p>}
      {page < pages && (
        <button type="button" className={`${secondaryButtonClass} mt-2`} onClick={() => void loadNext()} disabled={loading || disabled}>
          {loading ? 'Loading…' : 'Load more customers'}
        </button>
      )}
    </div>
  );
}
